import { Component, inject, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { LanguageDto } from '../../../../../shared/models/info/language-dto';
import { LanguageApiService } from '../../../../services/api/language-api.service';
import { FormsModule } from '@angular/forms';
import { TranslationService } from '../../../../services/translation.service';
import { NgClass } from '@angular/common';
import { SwPartNumberService } from '../../../../services/sw-part-number.service';
import { UnsavedChangesTrackerService } from '../../../../services/unsaved-changes-tracker.service';
import { errorToString } from '../../../../common/common-utils';

@Component({
  selector: 'app-language-selection',
  imports: [FormsModule, NgClass],
  templateUrl: './language-selection.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './language-selection.component.scss',
})
export class LanguageSelectionComponent implements OnInit, OnChanges {
  @Input() public displayAllLanguages = false;
  public languages: LanguageDto[] = [];
  public selectedLanguageCodeId: string | null = null;

  private readonly languageApiService = inject(LanguageApiService);
  private readonly translationService = inject(TranslationService);
  private readonly swPartNumberService = inject(SwPartNumberService);
  private readonly unsavedChangesService = inject(UnsavedChangesTrackerService);

  public ngOnInit(): void {
    this.fetchLanguages();
    this.swPartNumberService.swPartNumberChangeSubject.subscribe(() => {
      this.fetchLanguages();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['displayAllLanguages']) {
      this.fetchLanguages();
    }
  }

  public onSelect(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const nextLanguageCodeId = selectElement.value;

    const blocked = this.unsavedChangesService.areThereAnyUnsavedChanges(
      () => {
        this.handleLanguageSelection(nextLanguageCodeId);
      },
      () => {
        const currentCommitted = this.translationService.getLanguage();
        this.selectedLanguageCodeId = currentCommitted ?? null;
        selectElement.value = currentCommitted ?? '';
      }
    );

    if (!blocked) {
      this.handleLanguageSelection(nextLanguageCodeId);
    }
  }

  private fetchLanguages(): void {
    const nwNumberErrorMemoryId = this.displayAllLanguages
      ? undefined
      : this.swPartNumberService.getSwPartNumber()?.nwNumberErrorMemoryId;
    this.languageApiService.getLanguages(nwNumberErrorMemoryId).subscribe({
      next: (languages) => {
        this.languages = languages;
        const selectedLanguageCodeId = this.translationService.getLanguage();
        if (languages.some((lang) => lang.languageCodeId === selectedLanguageCodeId)) {
          this.selectedLanguageCodeId = selectedLanguageCodeId;
        } else {
          this.selectedLanguageCodeId = languages.length > 0 ? (languages[0].languageCodeId ?? null) : null;
          if (this.selectedLanguageCodeId) {
            this.translationService.setLanguage(this.selectedLanguageCodeId);
          }
        }
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private handleLanguageSelection(languageCodeId: string): void {
    this.selectedLanguageCodeId = languageCodeId;
    this.translationService.setLanguage(languageCodeId);
  }
}
